import { FlowStep } from '@varo/graphql-schema-types';
import { router, Stack, useLocalSearchParams } from 'expo-router';
import { redirectUserToTargetRoute } from 'features/onboarding/utils/redirectUserToTargetRoute';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BackHandler, StyleSheet, View } from 'react-native';
import UpsertFlowStep from 'services/graphql/mutations/onboarding/UpsertFlowStep.mutation';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { navigateBack } from 'shared/helpers/backNavigation.helpers';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useRefreshSystemBanners } from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import { useEngagement } from 'shared/hooks/useBraze';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation';
import SuccessScreen from 'shared/patterns/vxl/SuccessScreen/SuccessScreen';
import theme from 'shared/styles/theme';
import { formatCurrency } from 'shared/utils/string/currency';
import useVaroOnboardingFlow from 'shared/utils/useVaroOnboardingFlow/useVaroOnboardingFlow';

const SOURCE_PROGRESS_TRACKER = 'progress-tracker-funding';

type TransferSuccessSearchParams = {
  amount: string;
  fromAccountId: string;
  toAccountId: string;
  fromAccountType: string;
  toAccountType: string;
  fromBank: string;
  toBank: string;
  transferType: string;
  flowId?: string;
  source?: string;
};

const TransferSuccess = () => {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'transferStrings',
  });

  const {
    amount,
    fromAccountId,
    toAccountId,
    fromAccountType,
    toAccountType,
    fromBank,
    toBank,
    transferType,
    flowId,
    source,
  } = useLocalSearchParams<TransferSuccessSearchParams>();
  const [, submitUpsertFlowStep] = useCancellableMutation<{
    upsertFlowStep: FlowStep;
  }>(UpsertFlowStep);

  const flow = useVaroOnboardingFlow();

  const transfersAnalyticsCustomAttributes = {
    from_account_id: fromAccountId,
    to_account_id: toAccountId,
    from_account_type: fromAccountType,
    to_account_type: toAccountType,
    from_bank: fromBank,
    to_bank: toBank,
    transfer_amount: amount,
    transfer_type: transferType,
  };
  const { requestedCard, setRequestedCardId } = useEngagement();

  useEffect(() => {
    setRequestedCardId?.('transfer_success_screen_offer');
  }, [setRequestedCardId]);

  const { refreshSystemBanners } = useRefreshSystemBanners();

  const transferMoneySuccessful = usePressableAnalytics({
    analyticsId: 'transferMoneySuccessful',
    customAttributes: transfersAnalyticsCustomAttributes,
  });

  useEffect(() => {
    refreshSystemBanners();
    transferMoneySuccessful();
  }, [refreshSystemBanners, transferMoneySuccessful]);

  useEffect(() => {
    const backHandler = BackHandler.addEventListener(
      'hardwareBackPress',
      () => true,
    );
    return () => backHandler.remove();
  }, []);

  const handleDone = async () => {
    if (flowId) {
      try {
        const result = await submitUpsertFlowStep({
          input: { id: flowId },
        });
        if (!result.error) {
          redirectUserToTargetRoute(result.data?.upsertFlowStep);
        }
      } catch (error) {
        if (error instanceof AbortError) {
          return;
        }
        throw error;
      }
    } else if (source === SOURCE_PROGRESS_TRACKER) {
      router.replace('/');
    } else {
      navigateBack();
    }
  };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: false,
          gestureEnabled: false,
        }}
      />
      <SuccessScreen
        primaryButton={{
          label: translatedText('doneButton'),
          onPress: () => {
            void handleDone();
          },
        }}
        secondaryButton={{
          label: translatedText('secondaryButton'),
          onPress: () => {
            router.replace({
              pathname: `/${flow}/transfer/transfer-history`,
            });
          },
        }}
        eyebrowText={translatedText('eyebrowText')}
        visual="PLANE_A_ANIMATED"
        titleText={`${formatCurrency(amount, true)}${translatedText('successTitle')}`}
        content={
          <BodyBaseText numberOfLines={0} style={styles.contentText}>
            {translatedText('successSubText')}
          </BodyBaseText>
        }
        productOfferCard={requestedCard}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  contentText: {
    textAlign: 'center',
  },
});

export default TransferSuccess;
